"use client";

import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import {
  MATE_KURS_TO_MA,
  type ResolverResult,
} from "@/lib/formelblad-shared";

const STORAGE_KEY = "vektr.formelbladChoice";

type LocalChoice = { kursKod: string; ts: number };

function readLocalChoice(): LocalChoice | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as LocalChoice;
    if (
      !parsed ||
      typeof parsed.kursKod !== "string" ||
      typeof parsed.ts !== "number"
    ) {
      return null;
    }
    if (!MATE_KURS_TO_MA[parsed.kursKod]) return null;
    return parsed;
  } catch {
    return null;
  }
}

function detectMateKursFromPathname(pathname: string): string | null {
  const match = pathname.match(/\/kurser\/([^/]+)/);
  if (!match) return null;
  const kod = match[1].toUpperCase();
  return MATE_KURS_TO_MA[kod] ? kod : null;
}

/**
 * Resolves the user's "active" MATE kurs the same way FormelbladButton does:
 * URL match > server-confirmed single registration > localStorage+server-ts >
 * fallback MATE1A00X. Returns the MATE kurs-kod (or null while loading).
 *
 * Server fetch only fires when `enabled === true` and the URL doesn't already
 * carry a kurs-kod — keeps the network quiet on unrelated pages.
 */
export function useFormelbladKurs(enabled: boolean): string | null {
  const pathname = usePathname();
  const urlKursKod = detectMateKursFromPathname(pathname || "");
  const [resolved, setResolved] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) return;
    if (urlKursKod) return;
    let cancelled = false;

    fetch("/api/formelblad/resolve", { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : null))
      .then((res: ResolverResult | null) => {
        if (cancelled) return;
        if (!res) {
          setResolved("MATE1A00X");
          return;
        }
        if (res.source === "single" && res.kursKod) {
          setResolved(res.kursKod);
          return;
        }
        const merged: Record<string, number> = { ...res.serverTs };
        const localChoice = readLocalChoice();
        if (localChoice) {
          merged[localChoice.kursKod] = Math.max(
            merged[localChoice.kursKod] ?? 0,
            localChoice.ts,
          );
        }
        let winner: string | null = null;
        let winnerTs = 0;
        for (const [kod, ts] of Object.entries(merged)) {
          if (ts > winnerTs && MATE_KURS_TO_MA[kod]) {
            winner = kod;
            winnerTs = ts;
          }
        }
        setResolved(winner ?? "MATE1A00X");
      })
      .catch(() => {
        if (!cancelled) setResolved("MATE1A00X");
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, urlKursKod]);

  return urlKursKod ?? resolved;
}
